import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { AppConfig, AppConfigService } from './app-config.service';
import { UnauthorizedScope, UnauthorizedStateService } from './auth/unauthorized-state.service';
import { AuthSessionService } from './auth/auth-session.service';
import { TokenClaimsService } from './auth/token-claims.service';

/* 
we need to use this service as a workaround,
since EDC ts client library uses fetch for all requests,
and they doesn't provide a feature to include custom headers such as Authorization header
*/
@Injectable({
  providedIn: 'root',
})
export class FetchInterceptorService {
  constructor(
    private appConfig: AppConfigService,
    private oauthService: OAuthService,
    private unauthorizedState: UnauthorizedStateService,
    private authSession: AuthSessionService,
    private tokenClaims: TokenClaimsService,
  ) {}

  static isBackendUrl = (url: string, config: AppConfig) => {
    return url.startsWith(config.catalogUrl) || url.startsWith(config.managementApiUrl);
  };

  /**
   * Rewrites a management API URL to include the participant context ID.
   *
   * The EDC connector client library appends versioned paths to the base managementApiUrl,
   * so by the time this interceptor sees the request, the URL looks like:
   *   <managementApiUrl>/v5beta/assets  →  <managementApiUrl>/v5beta/participants/<id>/assets
   *
   * Falls back to inserting right after the base URL if no version segment is found:
   *   <managementApiUrl>/assets  →  <managementApiUrl>/participants/<id>/assets
   */
  injectParticipantContextId = (url: string, managementApiUrl: string, participantContextId: string): string => {
    const path = url.slice(managementApiUrl.length);
    // Try to match a version segment at the start of the path (e.g. /v5beta/, /v3/, /v2/)
    const withVersion = path.replace(/^\/(v[^/]+)\//, `/$1/participants/${participantContextId}/`);
    if (withVersion !== path) {
      // Version segment was found and rewritten
      return managementApiUrl + withVersion;
    }
    // No version segment — insert participants right after the base URL
    const separator = path.startsWith('/') ? '' : '/';
    return `${managementApiUrl}/participants/${participantContextId}${separator}${path}`;
  };

  convertInputToUrlString = (input: any) => {
    if (input && typeof input === 'object' && typeof input.url === 'string') {
      return input.url;
    }
    if (typeof URL !== 'undefined' && input instanceof URL) {
      return input.toString();
    }
    return String(input ?? '');
  }

  isNoAuthConfigured = (accessToken: string, config: AppConfig) => {
    return (accessToken == null || accessToken === "") && 
           (config.edcApiKey == null || config.edcApiKey === "");
  }

  cloneHeadersAndAddAuth = (accessToken: string, url: string, config: AppConfig, init?: RequestInit): RequestInit => {
    console.log('Intercepting fetch request:', url);

    let modifiedInit: RequestInit = { ...init };

    const modifiedHeaders = new Headers(modifiedInit?.headers || {});

    const originalContentType = init?.headers instanceof Headers
    ? init.headers.get('Content-Type')
    : init?.headers && 'Content-Type' in init.headers
        ? (init.headers as Record<string, string>)['Content-Type']
        : null;

    if (originalContentType) {
        modifiedHeaders.set('Content-Type', originalContentType);
    } else {
        modifiedHeaders.set('Content-Type', 'application/json');
    }

    if (accessToken) {
        modifiedHeaders.set('Authorization', `Bearer ${accessToken}`);
    } else {
        modifiedHeaders.set('X-Api-Key', config.edcApiKey ?? '');
    }

    modifiedInit.headers = modifiedHeaders;
    return modifiedInit;
  }

  initFetchInterceptor(): void {
    const originalFetch = window.fetch;

    window.fetch = (async (input: any, init?: any): Promise<Response> => {
        
      const config = this.appConfig.getConfig();
      if (!config) {
        console.warn('AppConfigService config is undefined.');
        return originalFetch(input, init);
      }

      let url = this.convertInputToUrlString(input);

      if (!FetchInterceptorService.isBackendUrl(url, config)) {
        return originalFetch(input, init);
      }

      // Rewrite management API URL to include the participant context ID from the token claim
      if (config.isVirtualConnector && url.startsWith(config.managementApiUrl)) {
        const participantContextId = this.tokenClaims.getParticipantContextId();
        if (participantContextId) {
          url = this.injectParticipantContextId(url, config.managementApiUrl, participantContextId);
          // Also rewrite the input so the request goes to the updated URL
          input = url;
        } else {
          console.warn('FetchInterceptorService: participant_context_id claim is missing from the token; sending request without participant path segment.');
        }
      }

      const accessToken = this.oauthService.getAccessToken();

      if (this.isNoAuthConfigured(accessToken, config)) {
        // assume that there's no authentication, all endpoints are public
        return originalFetch(input, init);
      }

      const modifiedInit = this.cloneHeadersAndAddAuth(accessToken, url, config, init);

      const response = await originalFetch(input, modifiedInit);

      const scope = this.resolveScope(url, config);
      if (response.status === 401) {
        this.authSession.handleAuthHttpStatus(response.status);
      }

      if (response.status === 401 || response.status === 403) {
        this.unauthorizedState.markUnauthorized(scope);
      } else if (response.ok) {
        this.unauthorizedState.clear(scope);
      }

      return response;
    }) as any;
  }

  private resolveScope(url: string, config: AppConfig): UnauthorizedScope {
    if (url.startsWith(config.catalogUrl)) {
      return 'catalog';
    }
    return 'management';
  }
}
